import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles, Typography, Button, TextField, Card, CardActionArea, CardContent, MenuItem, Accordion, AccordionSummary, AccordionDetails, Grid, Drawer } from '@material-ui/core';
import { Info as InfoIcon, Link as LinkIcon, LinkOff as LinkOffIcon } from "@material-ui/icons"
import { Redirect } from 'react-router'
import moment from "moment"
import "moment/locale/ja"
import withAuthorized from "../../With/withAuthorized"
import Welcome from "../Welcome"
import InfoPanel from '../../InfoPanel/InfoPanel';
import ConfirmDialog from '../../Utility/ConfirmDialog'
import { useTranslation } from "react-i18next"

import  * as Lambda from "../../../Lambda";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        textAlign: "left",
        margin: theme.spacing(1),
    },
    header: {        
        margin: theme.spacing(1),
    },
    accordion: {
        width: "200px",
        flexGrow: 1,
    },
    label: {
    },
    room: {
        margin: theme.spacing(1),
    },
    roomIn: {
        backgroundColor: "#fffa",
        padding: theme.spacing(1),
    },
    description: {
        minHeight: "3rem",
    },
    roomId: {
        textAlign: "right",
        fontSize: "0.5rem",
        color: "#000a",
        fontFamily: "monospace",
    },
    flex: {
        display: "flex",
    },
    spacer: {
        flexGrow: 1,
    },
    pre: {
        whiteSpace: "pre-wrap",
    }
  }),
);

const infoPanelWidth = 256;
const infoPanelStyles =  makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: infoPanelWidth,
      height: "100vh",
      zIndex: theme.zIndex.appBar - 1,
      paddingTop: 56, 
      paddingBottom: 56,
      [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: { 
        paddingTop: 48, 
        paddingBottom: 48,
      }, 
      [theme.breakpoints.up('sm')]: { 
        paddingTop: 64, 
        paddingBottom: 64,
      },
    }
  })
);

interface ThemeType {
    id: string
    url: string | undefined
}

interface Props {
}

function Rooms(props: Props) {

    const classes = useStyles()
    const infoPanelClasses = infoPanelStyles()
    const [t, i18n] = useTranslation()

    const [linkTo, setLinkTo] = useState<string|undefined>()

    useEffect(()=>{
        moment.locale(i18n.language)
    },[i18n.language])

    const [theme, setTheme] = useState<{[key:string]: ThemeType}>({})
    useEffect(()=>{
        const func = async ()=>{
            const response = await fetch("/Theme/Theme.json")
            if (response.ok){
                setTheme(await response.json())
            }
        }
        func()
    },[])

    const [rooms, setRooms] = useState<Lambda.RoomListType>([])
    const [next, setNext] = useState<string | undefined>()
    const nextRoom = async ()=>{
        setNext(undefined)
        const response = await Lambda.getRooms({next: next})
        setRooms([...rooms,...response.rooms])
        setNext(response.next)
    }
    useEffect(()=>{
        nextRoom()
    },[])

    const [input, setInput] = useState<{
        name: string,
        description: string,
        theme: string,
    }>({name:"",description:"", theme:"none"})
    const create = async ()=>{
        
        const response = await Lambda.createRoom({
            name: input.name,
            description: input.description,
            theme: input.theme
        })
        setRooms([response, ...rooms])
    }
    const [infoPanel, setInfoPanel] = useState(false)

    const [confirm, setConfirm] = useState<Lambda.RoomType|undefined>()
    const link = async (room: Lambda.RoomType) => {
        console.log(room)
        if (room.join) {
            setLinkTo(`/rooms/${room.id}`)
        }
        else {
            setConfirm(room)
        }
    }
    const join = async (result?: string) => {
        if (result === "Yes"){
            if (confirm && confirm.memberId) {
                await Lambda.joinRoom({
                    roomId: confirm.id, 
                    memberId: confirm.memberId,
                })
                setLinkTo(`/rooms/${confirm.id}`)
            }
        }
        else {
            setConfirm(undefined)
        }
    }

    return (
        <div className={classes.root}>
            <div style={infoPanel? {paddingRight: infoPanelWidth} : {}}>
                <div className={classes.header}>  
                    <Grid container direction="row-reverse" spacing={2} >
                        <Grid item>
                            <Button variant="contained" size="large" onClick={()=>setInfoPanel(!infoPanel)}>
                                    <InfoIcon />
                            </Button>
                        </Grid>
                        <Grid item className={classes.accordion}>
                            <Accordion>
                                <AccordionSummary
                                    className={classes.label}
                                >
                                    <Typography>{t("message.rooms.accordionRoom")}</Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    style={{backgroundImage: theme?.[input?.theme || "default"]?.url}}
                                >
                                    <div className={classes.room}>
                                        <div className={classes.roomIn}>
                                            <TextField required fullWidth label={t("label.room.name")}
                                                value={input.name} 
                                                onChange={v=>setInput({...input, name: v.target.value})} />
                                            <TextField fullWidth label={t("label.room.description")} multiline rows={3}
                                                value={input.description} 
                                                onChange={v=>setInput({...input, description: v.target.value})}  />
                                            <TextField fullWidth label={t("label.room.theme")} select 
                                                value={input.theme} 
                                                onChange={v=>setInput({...input, theme: v.target.value})} >
                                                {
                                                    Object.entries(theme).map(([key, value])=>(
                                                        <MenuItem key={key} value={key}>
                                                            <Typography>{t(`theme.${value.id}`)}</Typography>
                                                        </MenuItem>
                                                    ))
                                                }
                                            </TextField>
                                            
                                            <Button variant="contained" color="primary" disabled={input.name === ""} onClick={create}>
                                                {t("message.rooms.createRoom")}
                                            </Button>
                                        </div>
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </div>
                <div>
                    {
                        rooms.map(room=>(
                            <Card
                                key={room.id}
                                className={classes.room}
                                style={{backgroundImage: theme?.[room?.theme || "default"]?.url}}
                            >
                                <CardActionArea onClick={()=>{
                                    link(room)
                                }}>
                                    <CardContent>
                                        <div className={classes.roomIn}>
                                            <div className={classes.flex}>
                                                <Typography variant="h5" component="h2">
                                                    {room.name}
                                                </Typography>
                                                <span className={classes.spacer}/>
                                                {room.join ? <LinkIcon/> : <LinkOffIcon color="secondary"/>}
                                            </div>
                                            <div className={classes.description}>
                                                <Typography variant="body2" className={classes.pre}>{room.description}</Typography>
                                            </div>
                                            <Typography className={classes.roomId}>
                                                {moment(room.lastAccess).format("LLL")}
                                            </Typography>                                        
                                            <Typography className={classes.roomId}>
                                                {room.id}
                                            </Typography>
                                        </div>
                                    </CardContent>
                                </CardActionArea>
                            </Card>))
                    }
                    <Button disabled={next === undefined} onClick={nextRoom}>{t("message.rooms.nextPage")}</Button>
                </div>
            </div>
            <ConfirmDialog
                title={t("message.rooms.join.title")}
                message={t("message.rooms.join.message")}
                open={confirm !== undefined}
                onClick={(result)=>join(result)}
            ></ConfirmDialog>
            <Drawer open={infoPanel} variant="persistent" anchor="right" classes={infoPanelClasses}>
                <InfoPanel />
            </Drawer>            
            {
                linkTo ? (<Redirect push to={linkTo} />) : null
            }
        </div>
    )
}

export default withAuthorized<Props>(Rooms, Welcome)